import React, { useState } from "react";

const QuickSpinPanel = ({
  name,
  onRename,
  onNotify,
  entries,
  results,
  activeTab,
  setActiveTab,
  settings,
  onSettingsChange,
  onAddEntry,
  onEditEntry,
  onDeleteEntry,
  onClearEntries,
  onShuffle,
  onSort,
  onClearResults,
  isSpinning,
  showAdvanced,
  setShowAdvanced,
}) => {
  const [newEntry, setNewEntry] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [armedDeleteId, setArmedDeleteId] = useState(null);
  const [armedClearAll, setArmedClearAll] = useState(false);
  const armTimeoutRef = React.useRef(null);
  const clearAllTimeoutRef = React.useRef(null);

  const handleClearAllTap = () => {
    if (armedClearAll) {
      if (clearAllTimeoutRef.current) clearTimeout(clearAllTimeoutRef.current);
      setArmedClearAll(false);
      onClearEntries?.();
      return;
    }
    setArmedClearAll(true);
    clearAllTimeoutRef.current = setTimeout(() => setArmedClearAll(false), 2500);
  };

  const handleDeleteTap = (id) => {
    if (armedDeleteId === id) {
      if (armTimeoutRef.current) clearTimeout(armTimeoutRef.current);
      setArmedDeleteId(null);
      onDeleteEntry(id);
      return;
    }
    if (armTimeoutRef.current) clearTimeout(armTimeoutRef.current);
    setArmedDeleteId(id);
    armTimeoutRef.current = setTimeout(() => setArmedDeleteId(null), 2500);
  };

  const handleAdd = () => {
    const raw = newEntry.trim();
    if (!raw) return;

    const names = raw
      .split(",")
      .map((n) => n.trim())
      .filter((n) => n.length > 0);

    if (names.length === 0) {
      onNotify?.("Enter at least one name", "error");
      return;
    }
    if (entries.length + names.length > 25) {
      onNotify?.("Maximum 25 names allowed on a wheel", "error");
      return;
    }

    onAddEntry(names);
    setNewEntry("");
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditValue(entry.text);
  };

  const commitEdit = () => {
    const trimmed = editValue.trim();
    if (trimmed && editingId) {
      onEditEntry(editingId, trimmed);
    }
    setEditingId(null);
    setEditValue("");
  };

  React.useEffect(() => {
    return () => {
      if (armTimeoutRef.current) clearTimeout(armTimeoutRef.current);
      if (clearAllTimeoutRef.current) clearTimeout(clearAllTimeoutRef.current);
    };
  }, []);

  return (
    <div className="flex flex-col h-full bg-slate-900/80 rounded-xl border border-slate-700/60 overflow-hidden">
      {/* Wheel name */}
      <div className="px-3 pt-3 pb-2 border-b border-slate-700/60">
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
          Wheel Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => onRename(e.target.value)}
          placeholder="Untitled Wheel"
          maxLength={40}
          aria-label="Wheel name"
          className="w-full bg-transparent text-white font-semibold text-base outline-none placeholder-slate-500 border-b border-slate-700 focus:border-blue-500 pb-1 transition"
        />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-700/60">
        <button
          type="button"
          onClick={() => setActiveTab("entries")}
          className={`flex-1 py-2.5 text-sm font-medium transition ${
            activeTab === "entries"
              ? "text-white border-b-2 border-blue-500 bg-slate-800/50"
              : "text-slate-400 hover:text-slate-200"
          }`}
          aria-selected={activeTab === "entries"}
        >
          Entries{" "}
          <span className="ml-1 text-xs bg-slate-700 px-1.5 py-0.5 rounded-full">
            {entries.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("results")}
          className={`flex-1 py-2.5 text-sm font-medium transition ${
            activeTab === "results"
              ? "text-white border-b-2 border-blue-500 bg-slate-800/50"
              : "text-slate-400 hover:text-slate-200"
          }`}
          aria-selected={activeTab === "results"}
        >
          Results{" "}
          <span className="ml-1 text-xs bg-slate-700 px-1.5 py-0.5 rounded-full">
            {results.length}
          </span>
        </button>
      </div>

      {/* Toolbar */}
      {activeTab === "entries" && (
        <div className="flex flex-wrap gap-2 p-3 border-b border-slate-700/40">
          <button
            type="button"
            onClick={onShuffle}
            disabled={isSpinning || entries.length < 2}
            className="w-fit px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 transition"
          >
            🔀 Shuffle
          </button>
          <button
            type="button"
            onClick={() => onSort("asc")}
            disabled={isSpinning || entries.length < 2}
            className="w-fit px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 transition"
          >
            🔤 Sort
          </button>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="w-fit px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-700 hover:bg-slate-600 transition"
            aria-expanded={showAdvanced}
          >
            ⚙️ Advanced
          </button>
        </div>
      )}

      {/* Advanced panel */}
      {showAdvanced && activeTab === "entries" && (
        <div className="px-3 py-2 bg-slate-800/60 border-b border-slate-700/40 space-y-2 text-sm">
          {[
            { key: "allowDuplicates", label: "Allow duplicate entries" },
            { key: "removeAfterSpin", label: "Remove selected entry after spin" },
            { key: "preventRepeat", label: "Prevent repeat winner" },
            { key: "soundEnabled", label: "Sound on win" },
            { key: "confettiEnabled", label: "Confetti on win" },
          ].map((opt) => (
            <label
              key={opt.key}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={!!settings[opt.key]}
                onChange={(e) =>
                  onSettingsChange({ [opt.key]: e.target.checked })
                }
                className="rounded border-slate-500"
              />
              <span className="text-slate-200">{opt.label}</span>
            </label>
          ))}

          <div className="pt-2 mt-2 border-t border-slate-700/50">
            <p className="text-slate-300 mb-1.5">🏆 Bonus Rankings mode</p>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rankingMode"
                  checked={(settings.rankingMode || "fresh") === "fresh"}
                  onChange={() => onSettingsChange({ rankingMode: "fresh" })}
                  className="border-slate-500"
                />
                <span className="text-slate-200">
                  Fresh each spin (winner + shuffled rest)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rankingMode"
                  checked={settings.rankingMode === "leaderboard"}
                  onChange={() => onSettingsChange({ rankingMode: "leaderboard" })}
                  className="border-slate-500"
                />
                <span className="text-slate-200">
                  Persistent leaderboard (wins add up over time)
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 min-h-[120px]">
        {activeTab === "entries" ? (
          entries.length === 0 ? (
            <p className="text-center text-slate-500 text-sm py-6">
              No names loaded yet
            </p>
          ) : (
            <ul className="space-y-1.5">
              {entries.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-800/50 text-sm"
                >
                  {editingId === entry.id ? (
                    <>
                      <input
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitEdit();
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        className="flex-1 bg-slate-900 border border-blue-500 rounded px-2 py-1 text-white outline-none"
                      />
                      <button
                        type="button"
                        onClick={commitEdit}
                        className="w-fit p-1.5 text-emerald-400 hover:bg-slate-700 rounded"
                        aria-label="Save name"
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="w-fit p-1.5 text-slate-400 hover:bg-slate-700 rounded"
                        aria-label="Cancel edit"
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-slate-100 truncate">
                        {entry.text}
                      </span>
                      <button
                        type="button"
                        onClick={() => startEdit(entry)}
                        disabled={isSpinning}
                        className="w-fit p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded disabled:opacity-30"
                        aria-label={`Edit ${entry.text}`}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteTap(entry.id)}
                        disabled={isSpinning}
                        className={`w-fit p-1.5 rounded disabled:opacity-30 transition text-xs font-medium ${
                          armedDeleteId === entry.id
                            ? "bg-red-500/20 text-red-400 px-2"
                            : "text-slate-400 hover:text-red-400 hover:bg-slate-700"
                        }`}
                        aria-label={
                          armedDeleteId === entry.id
                            ? `Confirm delete ${entry.text}`
                            : `Delete ${entry.text}`
                        }
                      >
                        {armedDeleteId === entry.id ? "Confirm?" : "🗑️"}
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )
        ) : (
          <ul className="space-y-1.5">
            {results.map((r, idx) => (
              <li
                key={`${r.entryId}-${r.spinNumber}-${idx}`}
                className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-800/50 text-sm"
              >
                <span className="text-slate-500 w-6">{idx + 1}.</span>
                <span className="flex-1 text-slate-100">{r.entryName}</span>
              </li>
            ))}
            {results.length === 0 && (
              <p className="text-center text-slate-500 text-sm py-8">
                No spins yet
              </p>
            )}
          </ul>
        )}
      </div>

      {/* Footer actions */}
      <div className="p-3 border-t border-slate-700/40 space-y-2">
        {activeTab === "entries" ? (
          <div className="space-y-2">
            <textarea
              value={newEntry}
              onChange={(e) => setNewEntry(e.target.value)}
              placeholder="Add one name, or several separated by commas&#10;e.g. Sanjeev, Khushi, Babita"
              style={{height: "250px"}}
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 outline-none focus:border-blue-500 resize-none"
              disabled={isSpinning}
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={isSpinning || !newEntry.trim()}
              className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-lg text-sm font-medium transition"
            >
              Add Names
            </button>
            {entries.length > 0 && (
              <button
                type="button"
                onClick={handleClearAllTap}
                disabled={isSpinning}
                className={`w-full py-2 text-sm rounded-lg transition disabled:opacity-40 ${
                  armedClearAll
                    ? "bg-red-500/20 text-red-400 font-medium"
                    : "text-red-400/80 hover:bg-red-500/10"
                }`}
              >
                {armedClearAll ? "Tap again to clear all names" : "Clear Names"}
              </button>
            )}
            <p className="text-xs text-slate-500 text-center">
              Add 1 or more at a time · Up to 25 names total
            </p>
          </div>
        ) : (
          results.length > 0 && (
            <button
              type="button"
              onClick={onClearResults}
              className="w-full py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition"
            >
              Clear Results
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default QuickSpinPanel;